import React from "react";
import styled, { css } from "styled-components";
import { DownloadSimple } from "@phosphor-icons/react";

import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  DEFAULT_EGG_ID,
  FRAME_OUTER_WIDTH,
  FRAME_OUTER_HEIGHT,
  MARCS_PER_ROUND,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
} from "../../constants";
import { EyedropperProvider } from "../../contexts/EyedropperContext";
import { PaletteProvider, usePalette } from "../../contexts/PaletteContext";
import { ToolProvider, useTool } from "../../contexts/ToolContext";
import useAuthoring from "../../hooks/useAuthoring";
import useCreative from "../../hooks/useCreative";
import useDisplayedSurfaces from "../../hooks/useDisplayedSurfaces";
import useDrawing from "../../hooks/useDrawing";
import useKey from "../../hooks/useKey";
import useLandingCycle from "../../hooks/useLandingCycle";
import useMarcEngine from "../../hooks/useMarcEngine";
import useShowcase from "../../hooks/useShowcase";
import useSpeedrunCountIn from "../../hooks/useSpeedrunCountIn";
import useSpeedrunEngine from "../../hooks/useSpeedrunEngine";
import { LANDING_MARC_IDS } from "../../landingManifest";
import { AVAILABLE_MARC_IDS } from "../../marcManifest";
import {
  canCopyImageToClipboard,
  copyCompositeToClipboard,
  downloadComposite,
} from "../../composite";
import { eggImageUrl, marcImageUrl, referenceUrl } from "../../paths";
import { preloadReference } from "../../referenceCache";
import { SCORING_DEFAULTS } from "../../scoring";
import { readUrlState, MODES } from "../../urlParams";
import ActionButton from "../ActionButton";
import AuthorUIPanel from "../AuthorUIPanel";
import CompositeActionMenu from "../CompositeActionMenu";
import CreativePanel from "../CreativePanel";
import LandingUIPanel from "../LandingUIPanel";
import DebugScoringPanel from "../DebugScoringPanel";
import DrawingArea from "../DrawingArea";
import DrawingToolbar from "../DrawingToolbar";
import EndScreenModal from "../EndScreenModal";
import MarcPickerModal from "../MarcPickerModal";
import ReferencePhoto from "../ReferencePhoto";
import ScorePanel from "../ScorePanel";
import SyndicateModal from "../SyndicateModal";
import {
  buildOverrides,
  buildSubmissionBody,
} from "../../../shared/submission";
import { serializeStrokes } from "../../strokeSerialize";
import { attachSubmission, recordDrawing } from "../../myDrawings";

function App() {
  const [urlState] = React.useState(() => readUrlState());
  const [mode, setMode] = React.useState(urlState.mode);
  const authorMode = mode === MODES.AUTHOR;
  const speedrunMode = mode === MODES.SPEEDRUN;
  const landingMode = mode === MODES.LANDING;
  const creativeMode = mode === MODES.CREATIVE;

  // Preload every marc's photo + reference JSON at app mount so the
  // browser cache is warm before the landing reel cycles, the speedrun
  // lineup advances, or creative-mode marcs are picked. Without this,
  // marc swaps mid-transition can hit the network and the photo
  // <img> renders the previous (cached) image while the new one
  // decodes — the cloudflare flicker. Fire-and-forget; failed fetches
  // just mean a per-marc cache miss later, which the load-aware photo
  // gate still handles.
  React.useEffect(() => {
    for (const id of AVAILABLE_MARC_IDS) {
      const img = new Image();
      img.src = marcImageUrl(id);
      preloadReference(id);
    }
  }, []);

  // canvasRef is created here so useAuthoring's `save` can read the live
  // drawing canvas; AppBody attaches the ref to the actual <canvas>.
  const canvasRef = React.useRef(null);
  // Egg silhouette in canvas-virtual coords — populated by useEggLighting
  // (in DrawingArea), consumed by useDrawing's flood fill so the egg's
  // boundary acts as a wall.
  const eggSilhouetteRef = React.useRef(null);
  const authoring = useAuthoring({ urlState, mode, canvasRef });

  // Multi-marc round state. Only meaningful in speedrun mode; rolls a
  // fresh lineup each time speedrun activates and resets on exit.
  // ?marcs=001,003 pins the lineup for testing.
  const speedrun = useSpeedrunEngine({
    active: speedrunMode,
    available: AVAILABLE_MARC_IDS,
    pinnedLineup: urlState.pinnedMarcs,
    roundSize: MARCS_PER_ROUND,
  });

  // `lastDisplayMarcIdRef` is the "last non-null logicalMarcId we
  // were rendering." Declared here so useLandingCycle can read it as
  // `entryFromMarcId` BEFORE useDisplayedSurfaces (which mutates it
  // via effect) runs. Two consumers, one ref:
  //
  //   useLandingCycle    — reads on first activation to drive the
  //                        entry-from-other-mode transition (fade out
  //                        FROM marc → swap src → fade in NEW marc →
  //                        move egg). Without it, the photo snaps to
  //                        ids[0] on landing entry and the user sees
  //                        ids[0] fading 1→0 before the natural fade-
  //                        in. The cycle scopes the value to its own
  //                        first-activation render, so passing it
  //                        unconditionally is safe.
  //   useDisplayedSurfaces — uses it as the `effectiveMarcId` fallback
  //                          during transient null states (speedrun
  //                          pre-engine-start, creative pre-pick) and
  //                          keeps it in sync via effect.
  const lastDisplayMarcIdRef = React.useRef(null);

  // Landing-page reel. Cycles a separate marc pool (LANDING_MARC_IDS,
  // currently == AVAILABLE_MARC_IDS) with timed fade phases so the
  // page boots into a moving showcase instead of a static marc.
  const landing = useLandingCycle({
    ids: LANDING_MARC_IDS,
    active: landingMode,
    entryFromMarcId: lastDisplayMarcIdRef.current,
  });

  // Creative mode session state. Picker modal opens on entry; the
  // user's choice flows into effectiveMarcId via useDisplayedSurfaces.
  // Warmth/redshift/lights are reseeded to per-marc author values
  // inside useDisplayedSurfaces when the marc's reference loads.
  const creative = useCreative({ active: creativeMode });

  // Single source of truth for "what marc is showing on each surface."
  // Owns every marcId derivation: logicalMarcId selector,
  // effectiveMarcId, the three useReferenceJson fetches, displayedMarcId,
  // displayedMarcTransform, eggDisplay (with tween), the creative
  // cross-fade dampening, and the creative reseed effects. App's
  // PaletteProvider + drawingAuthoring + the speedrun count-in effect
  // pull what they need from here; AppBody receives the whole bag as a
  // single `surfaces` prop.
  const surfaces = useDisplayedSurfaces({
    urlState,
    mode,
    authoring,
    speedrun,
    landing,
    creative,
    lastDisplayMarcIdRef,
  });

  // Speedrun start gate + 3-2-1-GO count-in. Defers markStarted (the
  // round timer) until the egg has actually arrived at the round's
  // first marc, then plays the count-in and fires markStarted at GO.
  // See useSpeedrunCountIn for the gate's race-fix details.
  const countIn = useSpeedrunCountIn({
    active: speedrunMode,
    currentMarcId: speedrun.snapshot.currentMarcId,
    eggReferenceLoadedForMarcId: surfaces.eggReferenceLoadedForMarcId,
    tween: surfaces.tween,
    markStarted: speedrun.markStarted,
    debug: urlState.debug,
  });

  // Color is locked in modes where the player isn't picking colors —
  // speedrun (color follows the current target region) and landing
  // (palette would flicker per marc on the cycling reel). Creative
  // leaves it unlocked so the player can pick freely.
  const colorLocked = speedrunMode || landingMode;

  // Creative reuses DrawingArea's onion machinery (designed for
  // author mode) by feeding an authoring-shaped bag with just the
  // onion fields populated. Drag/transform setters are omitted —
  // the user can't reposition the marc onion in creative for now,
  // matching the "fewer, friendlier" toolset.
  const creativeAuthoringLike =
    creativeMode && creative.selectedMarcId
      ? {
          // The onion src lags via displayedMarcId so the photo stays
          // on the OLD marc through fade-out, snaps under cover of
          // opacity 0, and fades in as the NEW marc.
          marcOnionSrc: marcImageUrl(
            creative.displayedMarcId ?? creative.selectedMarcId,
          ),
          // No separate visibility toggle — the user's slider IS the
          // on/off switch, so visible = (user's) opacity > 0. We force
          // visible=true throughout the swap (even when creativeOnionOpacity
          // is 0) so the element stays mounted and CSS transition can
          // animate; otherwise OnionMarc unmounts at opacity 0 and the
          // fade-in has nothing to fade from.
          marcVisible: creative.onionOpacity > 0,
          marcOpacity: surfaces.creativeOnionOpacity,
          marcOpacityTransitionMs: surfaces.creativeOnionTransitionMs,
          marcTransformMode: false,
        }
      : null;
  const drawingAuthoring = creativeMode ? creativeAuthoringLike : authoring;

  return (
    <ToolProvider>
      <PaletteProvider
        palette={surfaces.eff.palette}
        setPaletteSlot={
          authorMode
            ? authoring.setPaletteSlot
            : creativeMode
              ? creative.setPaletteSlot
              : null
        }
        colorLocked={colorLocked}
      >
        <AppBody
          urlState={urlState}
          mode={mode}
          setMode={setMode}
          authoring={authoring}
          drawingAuthoring={drawingAuthoring}
          creative={creative}
          surfaces={surfaces}
          canvasRef={canvasRef}
          eggSilhouetteRef={eggSilhouetteRef}
          speedrun={speedrun}
          landing={landing}
          countIn={countIn}
        />
      </PaletteProvider>
    </ToolProvider>
  );
}

function AppBody({
  urlState,
  mode,
  setMode,
  authoring,
  drawingAuthoring,
  creative,
  surfaces,
  canvasRef,
  eggSilhouetteRef,
  speedrun,
  landing,
  countIn,
}) {
  // Pull the surface bundle apart locally so the JSX + effects below
  // read naturally. `surfaces` is the single source of truth for marcId
  // resolution + reference fetches + per-surface lagged transforms;
  // see useDisplayedSurfaces for the full timing model.
  const {
    effectiveMarcId,
    displayedMarcId,
    displayedMarcTransform,
    reference,
    eff,
    landingDrawing,
    eggDisplay,
    creativePhotoVisible,
    tween,
  } = surfaces;
  const eggTweening = tween.tweening;

  const { tool, size } = useTool();
  const { activeColorIndex, setActiveColorIndex, colorLocked } = usePalette();
  // Scoring knobs are deliberately ephemeral — we're hunting for values to
  // bake into constants.js, so surviving a refresh would just confuse
  // "is this the default or am I still overriding?". The debug panel that
  // mutates them is gated on ?debug.
  const [scoreOverrides, setScoreOverrides] = React.useState(SCORING_DEFAULTS);

  const drawing = useDrawing({
    canvasRef,
    tool,
    palette: eff.palette,
    colorIndex: activeColorIndex,
    size,
    eggSilhouetteRef,
    freezeStrokeColor: mode === MODES.CREATIVE,
  });

  // MarcEngine owns shownId / celebrating / target picker / auto-color /
  // auto-pen-on-celebration-end for the current marc. `active` gates the
  // engine on speedrun mode so committed flags don't carry over between
  // mode switches; the engine also resets on marcId change so flags
  // don't bleed across marcs in a round.
  // DrawingArea reads snapshot.celebrating to disable canvas input
  // during the celebration window — otherwise the auto-color-switch can
  // land mid-stroke and the player draws the next region's color onto
  // the previous one.
  const marcEngine = useMarcEngine({
    active: mode === MODES.SPEEDRUN,
    marcId: effectiveMarcId,
    strokes: drawing.strokes,
    palette: eff.palette,
    reference,
    scoreOverrides,
    lastTool: drawing.lastTool,
    lastActionAt: drawing.lastActionAt,
  });

  // Marc-completion showcase + end-screen-recap data. Fires after
  // EACH marc completes (not just at round end) — the inter-marc
  // punchline beat. See useShowcase for the full timing model.
  //
  // onMarcCaptured fires per marc the moment the showcase trigger
  // captures it — same beat as the recap snapshot. Used to record
  // the drawing into the local "Mine" log so the gallery's Mine tab
  // sees every speedrun entry, syndicated or not. The localId is
  // stashed so a later syndicate from the end-screen attaches to
  // the same entry instead of creating a duplicate.
  const roundLocalIdsRef = React.useRef({});
  const handleMarcCaptured = React.useCallback((marcId, snap) => {
    if (!snap.strokes?.length || !snap.palette) return;
    roundLocalIdsRef.current[marcId] = recordDrawing({
      marcId,
      eggId: DEFAULT_EGG_ID,
      source: "speedrun",
      elapsedMs: snap.duration,
      strokes: snap.strokes,
      palette: snap.palette,
    });
  }, []);
  const {
    showcasing,
    showcaseMarc,
    roundDrawings,
    triggerShowcase,
    debugEndScreenOpen,
    triggerDebugEndScreen,
  } = useShowcase({
    active: mode === MODES.SPEEDRUN,
    allDone: marcEngine.snapshot.allDone,
    advanceMarc: speedrun.advanceMarc,
    effectiveMarcId,
    eff,
    strokes: drawing.strokes,
    canvasRef,
    speedrunLineup: speedrun.snapshot.lineup,
    speedrunStartedAt: speedrun.snapshot.startedAt,
    onMarcCaptured: handleMarcCaptured,
  });

  // Wipe the canvas on marc change — including round entry (null →
  // first marc) and round exit (last marc → null) for speedrun, plus
  // every "Change Marc" action in creative. Idempotent on an empty
  // canvas, so the no-op cases are free.
  const currentMarcId = speedrun.snapshot.currentMarcId;
  const creativeSelectedMarcId =
    mode === MODES.CREATIVE ? creative.selectedMarcId : null;
  React.useEffect(() => {
    drawing.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMarcId, creativeSelectedMarcId]);

  // Debug-only: ←/→ in speedrun mode swap the current marc with the
  // prev/next from the manifest. Lets us land on a specific marc to
  // tune scoring without re-rolling. Bare arrow keys (no modifiers).
  const debugCycleEnabled = urlState.debug && mode === MODES.SPEEDRUN;
  useKey("ArrowLeft", () => {
    if (debugCycleEnabled) speedrun.cycleMarc(-1);
  });
  useKey("ArrowRight", () => {
    if (debugCycleEnabled) speedrun.cycleMarc(1);
  });

  // Debug-only: `r` toggles the easel between the marc photo and the
  // saved reference trace (reference.json's drawing.image). Lets us
  // eyeball "how big I drew his neck" against the canonical version
  // while tuning scoring. Off by default; resets every time the
  // current marc changes so you don't carry the override into a fresh
  // marc unintentionally.
  const [showReferenceDrawing, setShowReferenceDrawing] = React.useState(false);
  // Bare `r` is redo; shift+r is the debug-only easel toggle.
  useKey({ key: "r", shift: true }, () => {
    if (urlState.debug && mode === MODES.SPEEDRUN) {
      setShowReferenceDrawing((v) => !v);
    }
  });
  React.useEffect(() => {
    setShowReferenceDrawing(false);
  }, [effectiveMarcId]);

  // Debug-only: `v` (for "victory") fakes a marc completion in speedrun.
  // Blits the saved reference trace onto the canvas (so the showcase
  // animation has a "perfect player" drawing underneath the marc overlay
  // for alignment iteration) and fires triggerShowcase, which runs the
  // same setShowcasing → timer → advanceMarc chain as a real allDone.
  // The blit gets wiped automatically when advanceMarc fires the
  // currentMarcId-keyed drawing.clear() effect. `showcasing` gates the
  // early-return so the blit doesn't stack onto an in-flight showcase
  // (triggerShowcase self-guards against re-entry too).
  useKey("v", async () => {
    if (!urlState.debug || mode !== MODES.SPEEDRUN) return;
    if (!reference?.drawing?.image) return;
    if (showcasing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.src = reference.drawing.image;
    try {
      await img.decode();
    } catch {
      return;
    }
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    triggerShowcase();
  });

  // Debug-only: `n` (for "next round complete") fakes a round
  // completion and pops the end-screen modal. Picks 3 random marcs,
  // fetches their references, and builds the recap from each
  // reference.drawing.image so we can iterate on the modal without
  // grinding through real rounds. Strokes/palette aren't populated
  // (no real play happened); the modal renders from drawingSrc only.
  useKey("n", async () => {
    if (!urlState.debug || mode !== MODES.SPEEDRUN) return;
    const ids = [...AVAILABLE_MARC_IDS]
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    const fakes = {};
    for (const id of ids) {
      try {
        const r = await fetch(referenceUrl(id));
        if (!r.ok) continue;
        const ref = await r.json();
        if (!ref?.drawing?.image) continue;
        fakes[id] = {
          drawingSrc: ref.drawing.image,
          eggTransform: ref.egg?.transform ?? null,
          marcTransform: ref.marc?.transform ?? null,
        };
      } catch {
        // Skip this id; the modal handles missing tiles gracefully.
      }
    }
    if (Object.keys(fakes).length === 0) return;
    triggerDebugEndScreen(fakes);
  });

  // Right-click / mobile-button "save the marc you just made" menu.
  // anchorPos is null when closed, {x, y} (viewport-fixed) when opened
  // by a right-click on the drawing area. Mobile button bypasses the
  // menu and downloads directly.
  const [actionMenuPos, setActionMenuPos] = React.useState(null);
  const compositeOpts = React.useMemo(
    () => ({
      marcId: effectiveMarcId,
      eggId: DEFAULT_EGG_ID,
      strokes: drawing.strokes,
      palette: eff.palette,
      eggTransform: eggDisplay.eggTransform,
      marcTransform: eff.marcTransform,
      lights: eggDisplay.eggLights,
      warmth: eggDisplay.eggWarmth,
      redshift: eggDisplay.eggRedshift,
    }),
    [
      effectiveMarcId,
      drawing.strokes,
      eff.palette,
      eff.marcTransform,
      eggDisplay.eggTransform,
      eggDisplay.eggLights,
      eggDisplay.eggWarmth,
      eggDisplay.eggRedshift,
    ],
  );
  const actionMenuEnabled = mode !== MODES.LANDING && !!effectiveMarcId;
  const handleDrawingContextMenu = React.useCallback(
    (e) => {
      if (!actionMenuEnabled) return;
      e.preventDefault();
      setActionMenuPos({ x: e.clientX, y: e.clientY });
    },
    [actionMenuEnabled],
  );

  // Debug-only: `g` (for "guide") overlays the current target region's
  // mask + dilated search bbox onto the canvas. Shows where the
  // reference region actually lives, so we can tell whether the
  // player's drawing falls inside the search area or just slightly
  // outside it (which gives zero credit).
  const [showMaskOverlay, setShowMaskOverlay] = React.useState(false);
  useKey("g", () => {
    if (urlState.debug && mode === MODES.SPEEDRUN) {
      setShowMaskOverlay((v) => !v);
    }
  });
  React.useEffect(() => {
    setShowMaskOverlay(false);
  }, [effectiveMarcId]);

  // Build the debug overlay payload from the marcEngine snapshot's
  // `shown` (which carries searchBbox per subregion) joined with the
  // raw reference regions (which carry the mask data). Memoized so
  // the array identity is stable across renders that don't change
  // shown/reference, keeping DrawingArea's render quiet.
  const debugRegions = React.useMemo(() => {
    if (!showMaskOverlay) return null;
    const shown = marcEngine.snapshot.shown;
    if (!shown || !reference) return null;
    const refById = new Map(reference.drawing.regions.map((r) => [r.id, r]));
    return shown.subRegions.map((s) => {
      const ref = refById.get(s.id);
      return {
        id: s.id,
        mask: ref?.mask ?? null,
        bbox: ref?.bbox ?? null,
        searchBbox: s.searchBbox,
      };
    });
  }, [showMaskOverlay, marcEngine.snapshot.shown, reference]);

  // Tool hotkeys (q/b/w/f/e) live in DrawingToolbar so the same keypress
  // can drive both setTool and the popover-cycle (select → open → close).
  // Number-key palette switching is suppressed when colorLocked (speedrun);
  // see PaletteProvider's note. Hooks must be unconditional, so we gate
  // inside the handler.
  const pickColor = (i) => {
    if (colorLocked) return;
    setActiveColorIndex(i);
  };
  useKey("1", () => pickColor(0));
  useKey("2", () => pickColor(1));
  useKey("3", () => pickColor(2));
  useKey("4", () => pickColor(3));
  useKey("5", () => pickColor(4));
  useKey("6", () => pickColor(5));
  // Bare z, ⌘z, ctrl-z all undo; shift variants redo. `r` is also redo —
  // the hint chip on the redo button (⇧z renders awkwardly in the corner).
  useKey({ key: "z", shift: false }, () => drawing.undo(), {
    preventDefault: true,
  });
  useKey({ key: "z", shift: true }, () => drawing.redo(), {
    preventDefault: true,
  });
  useKey("r", () => drawing.redo(), { preventDefault: true });

  // Eyedropper: a live sample-from-marc-photo gesture invoked from the
  // Edit Color popover. The popover stays open during a session — hover
  // over the marc photo fires onPreview (so the editor can paint a
  // hover swatch), click fires onPick (commit). Session ends on
  // endEyedropper() (toggle off via the editor) or Escape (fires the
  // registered onCancel so the editor can reset its own UI state).
  const eyedropperRef = React.useRef(null);
  const [eyedropperActive, setEyedropperActive] = React.useState(false);
  const beginEyedropper = React.useCallback((handlers) => {
    eyedropperRef.current = handlers;
    setEyedropperActive(true);
  }, []);
  const endEyedropper = React.useCallback(() => {
    eyedropperRef.current = null;
    setEyedropperActive(false);
  }, []);
  const handlePickPixel = React.useCallback((hex) => {
    eyedropperRef.current?.onPick?.(hex);
  }, []);
  const handlePreviewPixel = React.useCallback((hex) => {
    eyedropperRef.current?.onPreview?.(hex);
  }, []);
  useKey("Escape", () => {
    const handlers = eyedropperRef.current;
    if (!handlers) return;
    eyedropperRef.current = null;
    setEyedropperActive(false);
    handlers.onCancel?.();
  });

  const UI_PANELS = {
    [MODES.AUTHOR]: (
      <AuthorUIPanel authoring={authoring} saveDisabled={!drawing.canUndo} />
    ),
    [MODES.LANDING]: (
      <LandingUIPanel setMode={setMode} onCreativeClick={creative.openPicker} />
    ),
    [MODES.SPEEDRUN]: (
      <ScorePanel
        snapshot={marcEngine.snapshot}
        bindBar={marcEngine.bindBar}
        roundSnapshot={speedrun.snapshot}
        countIn={countIn}
      />
    ),
    [MODES.CREATIVE]: (
      <CreativePanel
        creative={creative}
        onChangeMarc={creative.openPicker}
        onSyndicate={() =>
          setSyndicateTarget({
            marcId: effectiveMarcId,
            eggId: DEFAULT_EGG_ID,
            strokes: drawing.strokes,
            palette: eff.palette,
            source: "creative",
            // Creative-mode tweaks ride along so the gallery renders
            // the user's pose + lighting, not the canonical reference.
            // Speedrun submissions skip this and inherit the reference.
            overrides: buildOverrides({
              eggTransform: eggDisplay.eggTransform,
              marcTransform: eff.marcTransform,
              eggLights: eggDisplay.eggLights,
              warmth: eggDisplay.eggWarmth,
              redshift: eggDisplay.eggRedshift,
            }),
          })
        }
      />
    ),
  };

  // Syndicate target staged on click — null outside the flow, populated
  // with the strokes/palette/marc to submit when the user clicks
  // syndicate (creative panel button or per-marc end-screen button).
  // SyndicateModal opens whenever this is non-null.
  const [syndicateTarget, setSyndicateTarget] = React.useState(null);
  const handleSyndicateSubmit = React.useCallback(
    async ({ initials }) => {
      if (!syndicateTarget) return { ok: true };
      const wasSpeedrun = syndicateTarget.source === "speedrun";
      // Silent failure: any network/HTTP error is swallowed and the user
      // sees the same flow as success (modal closes). The local "Mine"
      // record only attaches when we have a real submission id.
      try {
        const res = await fetch("/api/submit", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(
            buildSubmissionBody({
              marcId: syndicateTarget.marcId,
              eggId: syndicateTarget.eggId,
              initials,
              source: syndicateTarget.source,
              elapsedMs: syndicateTarget.elapsedMs,
              strokes: serializeStrokes(syndicateTarget.strokes),
              palette: syndicateTarget.palette,
              overrides: syndicateTarget.overrides,
            }),
          ),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.id) {
          if (wasSpeedrun) {
            const localId = roundLocalIdsRef.current[syndicateTarget.marcId];
            if (localId) attachSubmission(localId, { id: data.id, initials });
          } else {
            const localId = recordDrawing({
              marcId: syndicateTarget.marcId,
              eggId: syndicateTarget.eggId,
              source: "creative",
              strokes: syndicateTarget.strokes,
              palette: syndicateTarget.palette,
              overrides: syndicateTarget.overrides,
            });
            attachSubmission(localId, { id: data.id, initials });
          }
        }
      } catch {}
      setSyndicateTarget(null);
      return { ok: true };
    },
    [syndicateTarget],
  );

  // Canvas input gate. `startedAt` is the canonical "speedrun is
  // running" signal — null through the entire pre-round window AND
  // through the first commit after mode flips to SPEEDRUN (before the
  // engine's effect runs `start()`). Scoping on `mode === SPEEDRUN`
  // (not `total > 0`) covers that first-commit window. Exit fade keeps
  // input enabled — markStarted has already fired, so the 200ms
  // fade-up is purely visual.
  const inputDisabled =
    mode === MODES.LANDING ||
    marcEngine.snapshot.celebrating ||
    eggTweening ||
    showcasing ||
    (mode === MODES.SPEEDRUN &&
      speedrun.snapshot.startedAt == null &&
      !countIn.exiting);

  // Easel photo visibility. In landing the cycle drives it; otherwise
  // the creative cross-fade dampening (or full opacity outside the
  // 'out' phase, derived in surfaces).
  const easelPhotoVisible =
    mode === MODES.LANDING ? landing.photoShown : creativePhotoVisible;

  // End-screen modal state. Real path opens when speedrun completes
  // AND the last marc's showcase has finished — the cross-fade plays
  // through before the modal pops, so the punchline lands first and
  // the recap follows. Debug path (`n` hotkey) forces it open with
  // fake roundDrawings; lineup + elapsedMs swap to the debug values
  // so the modal doesn't read "0:00" against fake data.
  const endScreenOpen =
    debugEndScreenOpen ||
    (mode === MODES.SPEEDRUN && speedrun.snapshot.complete && !showcasing);
  const endScreenLineup = debugEndScreenOpen
    ? Object.keys(roundDrawings)
    : speedrun.snapshot.lineup;
  const endScreenElapsedMs = debugEndScreenOpen
    ? 87654 // 01:27.6 — plausible-looking placeholder
    : speedrun.snapshot.startedAt != null && speedrun.snapshot.endedAt != null
      ? speedrun.snapshot.endedAt - speedrun.snapshot.startedAt
      : 0;

  const toolbarVisible = mode !== MODES.LANDING;

  return (
    <EyedropperProvider
      beginEyedropper={beginEyedropper}
      endEyedropper={endEyedropper}
      active={eyedropperActive}
    >
      <Page>
        <LeftBleed $visible={toolbarVisible} />
        <Layout>
          <ToolbarBackdrop />
          <ToolbarSlot $visible={toolbarVisible}>
            <DrawingToolbar
              onUndo={drawing.undo}
              onRedo={drawing.redo}
              canUndo={drawing.canUndo}
              canRedo={drawing.canRedo}
              disabled={!toolbarVisible}
              visible={toolbarVisible}
              showMute={mode === MODES.SPEEDRUN}
            />
          </ToolbarSlot>

          <ContentArea>
            <HeaderSlot>
              <Title
                $clickable={mode !== MODES.LANDING}
                onClick={() => {
                  if (mode === MODES.LANDING) return;
                  // Author mode is URL-gated, so going home means clearing
                  // the URL params — otherwise a refresh would bounce back
                  // into it.
                  if (mode === MODES.AUTHOR) {
                    window.location.assign("/");
                    return;
                  }
                  setMode(MODES.LANDING);
                }}
              >
                Marc Andreessen Egg Game
              </Title>
            </HeaderSlot>

            <DrawingSlot>
              <FrameBox onContextMenu={handleDrawingContextMenu}>
                <DrawingArea
                  eggSrc={eggImageUrl(DEFAULT_EGG_ID)}
                  eggTransform={eggDisplay.eggTransform}
                  eggLights={eggDisplay.eggLights}
                  eggWarmth={eggDisplay.eggWarmth}
                  eggRedshift={eggDisplay.eggRedshift}
                  marcTransform={displayedMarcTransform}
                  canvasRef={canvasRef}
                  handlers={drawing.canvasHandlers}
                  align="center"
                  authoring={drawingAuthoring}
                  eggSilhouetteRef={eggSilhouetteRef}
                  inputDisabled={inputDisabled}
                  debugRegions={debugRegions}
                  landingDrawing={landingDrawing}
                  showcaseMarc={showcaseMarc}
                />
                {/* Mounted at all sizes so a breakpoint flip doesn't
                  remount and reset cross-fade animations. */}
                <MobileReferenceCard>
                  <ReferencePhoto
                    withEasel={false}
                    marcId={displayedMarcId}
                    marcTransform={displayedMarcTransform}
                    referenceDrawingSrc={reference?.drawing?.image ?? null}
                    showReferenceDrawing={showReferenceDrawing}
                    photoVisible={easelPhotoVisible}
                    photoLoadGate={mode === MODES.LANDING}
                    showcasing={showcasing}
                    eyedropperActive={eyedropperActive}
                    onPickPixel={handlePickPixel}
                    onPreviewPixel={handlePreviewPixel}
                  />
                </MobileReferenceCard>
              </FrameBox>
              {mode === MODES.CREATIVE && actionMenuEnabled && (
                <MobileDownloadAction>
                  <ActionButton
                    type="button"
                    onClick={() => downloadComposite(compositeOpts)}
                    title="Download a PNG of this Marc"
                  >
                    <DownloadSimple size={14} weight="bold" />
                    <span>Download</span>
                  </ActionButton>
                </MobileDownloadAction>
              )}
            </DrawingSlot>

            <EaselSlot>
              <ReferencePhoto
                marcId={displayedMarcId}
                marcTransform={displayedMarcTransform}
                referenceDrawingSrc={reference?.drawing?.image ?? null}
                showReferenceDrawing={showReferenceDrawing}
                photoVisible={easelPhotoVisible}
                photoLoadGate={mode === MODES.LANDING}
                showcasing={showcasing}
                eyedropperActive={eyedropperActive}
                onPickPixel={handlePickPixel}
                onPreviewPixel={handlePreviewPixel}
              />
            </EaselSlot>

            <PanelSlot>{UI_PANELS[mode]}</PanelSlot>
          </ContentArea>

          <Footer $toolbarVisible={toolbarVisible}>
            <span>
              a website by{" "}
              <a
                href="https://eieio.games"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                }}
              >
                nolen (eieio)
              </a>
            </span>
            <Socials>
              <SocialLink
                href="https://bsky.app/profile/itseieio.bsky.social"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="eieio on Bluesky"
                title="eieio on Bluesky"
              >
                <BlueskyIcon size={14} />
              </SocialLink>
              <SocialLink
                href="https://x.com/itseieio"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="eieio on Twitter"
                title="eieio on Twitter"
              >
                <TwitterIcon size={14} />
              </SocialLink>
            </Socials>
          </Footer>
        </Layout>

        {urlState.debug && mode === MODES.SPEEDRUN && reference && (
          <DebugScoringPanel
            overrides={scoreOverrides}
            onChange={setScoreOverrides}
            shown={marcEngine.snapshot.shown}
            maskOverlay={showMaskOverlay}
            onToggleMask={() => setShowMaskOverlay((v) => !v)}
            marcId={effectiveMarcId}
          />
        )}

        {/* Picker is mountable from either mode:
            - In landing: the Creative button opens it. Picking flips
              mode to CREATIVE; dismissing leaves the user in landing
              with no state change. The mode flip happens here (not in
              the button's onClick) so a dismissal stays a no-op.
            - In creative: the "Change Marc" button opens it. Picking
              cross-fades to the new marc; dismissing keeps the user
              on the current marc.
          Picker pick always calls pickMarc with displayedMarcId as
          the fade-from hint — for the very first pick out of landing
          this is the ambient (landing's last) marc; for subsequent
          picks displayedMarcId is already populated and the hint is
          just a backup. */}
        {(mode === MODES.LANDING || mode === MODES.CREATIVE) && (
          <MarcPickerModal
            open={creative.pickerOpen}
            onOpenChange={(o) => {
              if (o) {
                creative.openPicker();
                return;
              }
              creative.closePicker();
            }}
            marcIds={AVAILABLE_MARC_IDS}
            selectedId={creative.selectedMarcId}
            onPick={(id) => {
              creative.pickMarc(id, displayedMarcId);
              if (mode === MODES.LANDING) setMode(MODES.CREATIVE);
            }}
          />
        )}

        {/* End-screen modal. Every close path (Keep Building / X / Esc /
          overlay) bails to landing — debug-`n` opens are also expected
          to land on dismiss, since the only debug flows are "test the
          full close" or "look at the modal." setMode(LANDING) flips
          useShowcase's `active` false, which clears roundDrawings +
          debugEndScreenOpen on its own. See the named locals above
          for open/lineup/elapsedMs derivation. */}
        <EndScreenModal
          open={endScreenOpen}
          onOpenChange={(next) => {
            if (next) return;
            setMode(MODES.LANDING);
          }}
          lineup={endScreenLineup}
          roundDrawings={roundDrawings}
          elapsedMs={endScreenElapsedMs}
          onSyndicate={(marcId, snapshot) =>
            setSyndicateTarget({
              marcId,
              eggId: DEFAULT_EGG_ID,
              strokes: snapshot.strokes,
              palette: snapshot.palette,
              source: "speedrun",
              elapsedMs: snapshot.duration,
            })
          }
        />

        <SyndicateModal
          open={syndicateTarget != null}
          onOpenChange={(next) => {
            if (!next) setSyndicateTarget(null);
          }}
          onSubmit={handleSyndicateSubmit}
        />

        <CompositeActionMenu
          open={actionMenuPos != null && actionMenuEnabled}
          onOpenChange={(next) => {
            if (!next) setActionMenuPos(null);
          }}
          anchorPos={actionMenuPos}
          onDownload={() => downloadComposite(compositeOpts)}
          onCopy={() => copyCompositeToClipboard(compositeOpts)}
          canCopy={canCopyImageToClipboard()}
        />
      </Page>
    </EyedropperProvider>
  );
}

// On tablet, ContentArea uses `display: contents` so its children
// flatten into Layout's grid — that's how the panel spans toolbar-
// right-edge to viewport-edge while the toolbar stays full-height.
const LAYOUT_MAX_WIDTH = 1600;
const TOOLBAR_TRANSITION_MS = 350;

const Page = styled.div`
  min-height: 100vh;
  min-height: 100svh;
  // Clips the toolbar's slide-off transform so it doesn't extend Page
  // bounds and trigger scroll. Layout itself is locked to 100svh.
  overflow: hidden;
`;

// Chrome strip on viewports wider than Layout. Renders inside React so
// it can fade in/out alongside the toolbar's slide animation.
const LeftBleed = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: max(0px, calc(50vw - ${LAYOUT_MAX_WIDTH / 2}px));
  height: 100%;
  background-color: var(--color-chrome);
  pointer-events: none;
  z-index: 0;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity ${TOOLBAR_TRANSITION_MS}ms ease;
`;

// Cream backdrop revealed when the toolbar slides off — only meaningful
// on tablet (col1 row3 under the panel) and mobile (toolbar's own
// track). On desktop the area behind the toolbar is graph paper, no
// backdrop needed.
const ToolbarBackdrop = styled.div`
  display: none;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    display: block;
    grid-row: 3;
    grid-column: 1;
    background-color: var(--color-orange-100);
    border-top: 1px solid var(--color-orange-300);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-area: toolbar;
    border-top: none;
  }
`;

const Layout = styled.main`
  width: 100%;
  max-width: ${LAYOUT_MAX_WIDTH}px;
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100svh;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) auto;
  grid-template-areas:
    "toolbar content"
    "toolbar footer";
  padding-right: 10px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-template-columns: auto minmax(0, 1.3fr) minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      "toolbar header  header"
      "toolbar drawing easel"
      "toolbar panel   panel"
      "toolbar footer  footer";
    padding-right: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto auto;
    grid-template-areas:
      "content"
      "toolbar"
      "footer";
    padding-right: 0;
  }
`;

const ToolbarSlot = styled.div`
  grid-area: toolbar;
  display: flex;
  align-items: stretch;
  min-height: 0;
  min-width: 0;
  position: relative;
  z-index: 1;
  transform: translateX(${(p) => (p.$visible ? "0" : "-100%")});
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? "auto" : "none")};
  transition:
    transform ${TOOLBAR_TRANSITION_MS}ms ease,
    opacity ${TOOLBAR_TRANSITION_MS}ms ease;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    transform: translateY(${(p) => (p.$visible ? "0" : "100%")});
  }
`;

const ContentArea = styled.div`
  grid-area: content;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  grid-template-columns: minmax(440px, 1.1fr) minmax(400px, 1fr);
  grid-template-areas:
    "header  header"
    "drawing easel"
    "drawing panel";
  padding: 0 1.25rem;
  max-height: 1200px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    display: contents;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "header"
      "drawing"
      "panel";
    padding: 0;
    max-height: none;
  }
`;

const Socials = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  transition: color 120ms ease-out;
  transform: translateY(1px);

  &:hover {
    color: var(--color-text);
  }

  &:focus-visible {
    outline: 2px solid var(--color-text);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

// Bluesky butterfly. Phosphor doesn't ship one.
function BlueskyIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364-3.911.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
    </svg>
  );
}

// Pre-X Twitter bird (deliberate — preferring the legacy logo).
function TwitterIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      style={{ transform: "scale(1.3)" }}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
    </svg>
  );
}

const Footer = styled.footer`
  grid-area: footer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0 12px 12px;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  letter-spacing: 0.01em;
  transition: background-color 200ms ease-out;
  transition-delay: 100ms;

  a {
    color: inherit;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.2em;
    font-weight: 700;

    &:hover {
      color: var(--color-text);
      text-decoration-thickness: 2px;
    }
  }

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    background-color: var(--color-orange-100);
    padding: 8px 12px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    /* border-top: 1px solid var(--color-orange-300); */
    padding: 2px 12px 6px;
    background-color: ${(p) =>
      p.$toolbarVisible
        ? "var(--color-orange-200)"
        : "var(--color-orange-100)"};
  }
`;

const HeaderSlot = styled.header`
  grid-area: header;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-bottom: 3.5rem;
  }
`;

const Title = styled.h1`
  /* Lower clamp floor lets narrow phones use a smaller title — the
     reference card sits high in the drawing area and a 40px title
     gives it less room to overlap. */
  font-size: clamp(2.2rem, 5.2vw, 5rem);
  line-height: 1;
  padding-bottom: 0.5rem;
  padding-top: 0.25rem;
  font-weight: 600;
  font-family: "Fraunces", serif;
  text-wrap: balance;
  text-align: center;
  letter-spacing: -0.01em;
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.5),
    0 2px 6px rgba(80, 45, 20, 0.18);

  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;
      &:hover {
        text-decoration: underline;
        text-decoration-thickness: 4px;
      }
    `}
`;

const DrawingSlot = styled.div`
  grid-area: drawing;
  position: relative;
  min-width: 0;
  min-height: 0;
  padding: 16px 1rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  container-type: size;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 8px 8px 1.5rem;
  }
`;

// Mobile-only download button under the drawing frame. Hidden on
// desktop — desktop users get the right-click menu, which also offers
// clipboard copy. Mobile gets a single direct-download affordance
// since clipboard support is patchy on phones.
const MobileDownloadAction = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    justify-content: center;
    margin-top: 1rem;
  }
`;

// Cap the frame's display width on mobile so the reference card's
// straddle has horizontal room (otherwise the card clips off the right
// edge on tall+narrow phones). Trades drawing area for card breathing.
// Tied to the card knobs: with REF_CARD_FRACTION × |X_SHIFT_FRAC| of
// frame width sticking out past the right edge, frame_max ≈
// 1 / (1 + 2 × that overhang) keeps the card inside the viewport.
const FRAME_MAX_W_FRAC_MOBILE = 0.85;

// Sized to the frame's actual aspect-fitted display bounds so
// MobileReferenceCard can position relative to the frame instead of
// the slot. container-type: size makes its descendants' cq* units
// resolve against this box (= the frame's display size).
const FrameBox = styled.div`
  position: relative;
  width: min(
    100cqw,
    calc(100cqh * ${FRAME_OUTER_WIDTH} / ${FRAME_OUTER_HEIGHT})
  );
  height: min(
    100cqh,
    calc(100cqw * ${FRAME_OUTER_HEIGHT} / ${FRAME_OUTER_WIDTH})
  );
  display: flex;
  container-type: size;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: min(
      ${FRAME_MAX_W_FRAC_MOBILE * 100}cqw,
      calc(100cqh * ${FRAME_OUTER_WIDTH} / ${FRAME_OUTER_HEIGHT})
    );
    height: min(
      100cqh,
      calc(
        ${FRAME_MAX_W_FRAC_MOBILE * 100}cqw * ${FRAME_OUTER_HEIGHT} /
          ${FRAME_OUTER_WIDTH}
      )
    );
  }
`;

// container-type: size feeds ReferencePhoto's Stage cqw/cqh sizing.
const EaselSlot = styled.div`
  grid-area: easel;
  min-width: min-content;
  min-height: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  container-type: size;
  container-name: easel-area;
  width: 100%;
  padding-bottom: 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const PanelSlot = styled.div`
  grid-area: panel;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-bottom: 1rem;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    padding: 0;
  }
`;

// ── Reference-card placement knobs (mobile only) ──
// Size as a fraction of the gold frame's display dimensions.
const REF_CARD_FRACTION = 0.38;
// Anchor point inside the frame: 100%/50% = right edge, vertical mid.
const REF_CARD_X = "90%";
const REF_CARD_Y = "13%";
// Translate past the anchor (-50%/-50% = card centered on the anchor,
// straddling the frame's right edge half-in / half-out).
const REF_CARD_X_SHIFT = "-50%";
const REF_CARD_Y_SHIFT = "-50%";

const MobileReferenceCard = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
    position: absolute;
    /* Largest aspect-preserving rectangle that fits within
       (fraction × frame-width) × (fraction × frame-height) — so the
       card shrinks with both axes of the frame, not just the
       viewport. cq* units resolve against FrameBox via its
       container-type. */
    width: min(
      ${REF_CARD_FRACTION * 100}cqw,
      calc(${REF_CARD_FRACTION * 100}cqh * ${CANVAS_WIDTH} / ${CANVAS_HEIGHT})
    );
    height: min(
      ${REF_CARD_FRACTION * 100}cqh,
      calc(${REF_CARD_FRACTION * 100}cqw * ${CANVAS_HEIGHT} / ${CANVAS_WIDTH})
    );
    left: ${REF_CARD_X};
    top: ${REF_CARD_Y};
    transform: translate(${REF_CARD_X_SHIFT}, ${REF_CARD_Y_SHIFT});
    container-type: size;
    z-index: 5;
    pointer-events: none;
  }
`;

export default App;
